import { useCallback, useMemo, useRef } from "react";

import { type ApolloError, type ApolloQueryResult, NetworkStatus, useQuery } from "@apollo/client";

import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { getRemoteAssets } from "../api/queries/getRemoteAssets";
import {
    type GetRemoteAssetsQuery,
    type GetRemoteAssetsQueryVariables,
} from "../api/queries/getRemoteAssets.gql-generated";

const PAGE_SIZE = 50;

interface RemoteAssetsResult {
    assets: GetRemoteAssetsQuery["remoteAssets"]["nodes"];
    hasMore: boolean;
    isLoading: boolean;
    isFetchingMore: boolean;
    error: ApolloError | undefined;
    loadMore: () => Promise<ApolloQueryResult<GetRemoteAssetsQuery>> | undefined;
}

export const useRemoteAssets = (searchQuery: string): RemoteAssetsResult => {
    const { orgId } = useSelectedOrganizationContainer();

    const variables = useMemo(
        (): GetRemoteAssetsQueryVariables => ({
            organizationId: orgId ?? "",
            query: searchQuery || null,
            cursor: null,
            limit: PAGE_SIZE,
        }),
        [orgId, searchQuery],
    );

    const { data, loading, error, fetchMore, networkStatus } = useQuery<
        GetRemoteAssetsQuery,
        GetRemoteAssetsQueryVariables
    >(getRemoteAssets, {
        variables,
        skip: !orgId,
        // cache-first is intentional: all mutations (delete, update, bulk
        // patch) update the cache directly, so the list stays consistent
        // within a session. cache-and-network would trigger a background
        // refetch that replaces accumulated pages with page 1 only.
        fetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    const assets = data?.remoteAssets.nodes ?? [];
    const cursor = data?.remoteAssets.cursor;
    const hasMore = !!cursor;
    const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
    const isLoading = isFetchingMore ? false : loading;

    const fetchingRef = useRef(false);

    const loadMore = useCallback(() => {
        if (!cursor || fetchingRef.current) return;
        fetchingRef.current = true;
        return fetchMore({
            variables: { ...variables, cursor },
            // updateQuery is required because type-policy merge alone does not
            // trigger useQuery re-renders in Apollo 3.9.x with this query
            // shape and cache-first policy (confirmed by local repro).
            // updateQuery forces the ObservableQuery to re-deliver results.
            // The merge function in cacheRemoteAssetsConfig has a cursor-aware
            // fallback for when this workaround is removed after an upgrade.
            updateQuery: (prev, { fetchMoreResult }) => ({
                ...fetchMoreResult,
                remoteAssets: {
                    ...fetchMoreResult.remoteAssets,
                    nodes: [...prev.remoteAssets.nodes, ...fetchMoreResult.remoteAssets.nodes],
                },
            }),
        }).finally(() => {
            fetchingRef.current = false;
        });
    }, [cursor, fetchMore, variables]);

    return {
        assets,
        hasMore,
        isLoading,
        isFetchingMore,
        error,
        loadMore,
    };
};
